import {
  ListItemText,
  ListSubheader,
  MenuItem,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { HTMLAttributes } from "react";
import { testIds } from "./StateSelect.testIds";

type StateSelectProps = TextFieldProps & {
  useAbbreviations?: boolean;
  showAllOption?: boolean;
  "data-testid"?: string;
};

const US_STATES = [
  { name: "Alabama", abbr: "AL" },
  { name: "Alaska", abbr: "AK" },
  { name: "Arizona", abbr: "AZ" },
  { name: "Arkansas", abbr: "AR" },
  { name: "California", abbr: "CA" },
  { name: "Colorado", abbr: "CO" },
  { name: "Connecticut", abbr: "CT" },
  { name: "Delaware", abbr: "DE" },
  { name: "Florida", abbr: "FL" },
  { name: "Georgia", abbr: "GA" },
  { name: "Hawaii", abbr: "HI" },
  { name: "Idaho", abbr: "ID" },
  { name: "Illinois", abbr: "IL" },
  { name: "Indiana", abbr: "IN" },
  { name: "Iowa", abbr: "IA" },
  { name: "Kansas", abbr: "KS" },
  { name: "Kentucky", abbr: "KY" },
  { name: "Louisiana", abbr: "LA" },
  { name: "Maine", abbr: "ME" },
  { name: "Maryland", abbr: "MD" },
  { name: "Massachusetts", abbr: "MA" },
  { name: "Michigan", abbr: "MI" },
  { name: "Minnesota", abbr: "MN" },
  { name: "Mississippi", abbr: "MS" },
  { name: "Missouri", abbr: "MO" },
  { name: "Montana", abbr: "MT" },
  { name: "Nebraska", abbr: "NE" },
  { name: "Nevada", abbr: "NV" },
  { name: "New Hampshire", abbr: "NH" },
  { name: "New Jersey", abbr: "NJ" },
  { name: "New Mexico", abbr: "NM" },
  { name: "New York", abbr: "NY" },
  { name: "North Carolina", abbr: "NC" },
  { name: "North Dakota", abbr: "ND" },
  { name: "Ohio", abbr: "OH" },
  { name: "Oklahoma", abbr: "OK" },
  { name: "Oregon", abbr: "OR" },
  { name: "Pennsylvania", abbr: "PA" },
  { name: "Rhode Island", abbr: "RI" },
  { name: "South Carolina", abbr: "SC" },
  { name: "South Dakota", abbr: "SD" },
  { name: "Tennessee", abbr: "TN" },
  { name: "Texas", abbr: "TX" },
  { name: "Utah", abbr: "UT" },
  { name: "Vermont", abbr: "VT" },
  { name: "Virginia", abbr: "VA" },
  { name: "Washington", abbr: "WA" },
  { name: "West Virginia", abbr: "WV" },
  { name: "Wisconsin", abbr: "WI" },
  { name: "Wyoming", abbr: "WY" },
];

const CANADA_STATES = [
  { name: "Alberta", abbr: "AB" },
  { name: "British Columbia", abbr: "BC" },
  { name: "Manitoba", abbr: "MB" },
  { name: "New Brunswick", abbr: "NB" },
  { name: "Newfoundland and Labrador", abbr: "NL" },
  { name: "Nova Scotia", abbr: "NS" },
  { name: "Ontario", abbr: "ON" },
  { name: "Prince Edward Island", abbr: "PE" },
  { name: "Quebec", abbr: "QC" },
  { name: "Saskatchewan", abbr: "SK" },
  { name: "Northwest Territories", abbr: "NT" },
  { name: "Nunavut", abbr: "NU" },
  { name: "Yukon", abbr: "YT" },
];

export default function StateSelect({
  useAbbreviations: controlledUseAbbreviations,
  showAllOption = false,
  value,
  ...props
}: StateSelectProps) {
  const { "data-testid": dataTestId, ...restProps } = props;
  const useAbbreviations = controlledUseAbbreviations;
  const defaultValue = showAllOption ? "ALL" : value;

  return (
    <TextField
      select
      value={value || defaultValue}
      {...restProps}
      SelectProps={
        dataTestId
          ? {
              ...restProps.SelectProps,
              SelectDisplayProps: {
                ...restProps.SelectProps?.SelectDisplayProps,
                "data-testid": dataTestId,
              } as HTMLAttributes<HTMLDivElement>,
            }
          : restProps.SelectProps
      }
      onChange={(event) => {
        if (event.target.value === "ALL") {
          event.target.value = "";
        }
        restProps.onChange?.(event);
      }}
    >
      {showAllOption && (
        <MenuItem
          data-testid={
            dataTestId
              ? `${dataTestId}-option-all`
              : testIds.stateSelectAllOption
          }
          value={"ALL"}
        >
          <ListItemText primary="All" />
        </MenuItem>
      )}
      <ListSubheader>United States</ListSubheader>
      {US_STATES.map((state) => (
        <MenuItem
          data-testid={
            dataTestId
              ? `${dataTestId}-option-${state.abbr.toLowerCase()}`
              : `${testIds.stateSelectUnitedStatesOption}-${state.abbr}`
          }
          key={state.abbr}
          value={useAbbreviations ? state.abbr : state.name}
        >
          <ListItemText primary={state.name} />
        </MenuItem>
      ))}
      <ListSubheader>Canada</ListSubheader>
      {CANADA_STATES.map((state) => (
        <MenuItem
          data-testid={
            dataTestId
              ? `${dataTestId}-option-${state.abbr.toLowerCase()}`
              : `${testIds.stateSelectCanadaOption}-${state.abbr}`
          }
          key={state.abbr}
          value={useAbbreviations ? state.abbr : state.name}
        >
          <ListItemText primary={state.name} />
        </MenuItem>
      ))}
    </TextField>
  );
}
